import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ENDPOINTS } from '@/config/endpoints';
import type { User } from '@/types/user';
import type { AppCard, AppCardAdmin } from '@/types/appCard';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/';

export type Settings = {
    defaultLanguage: string;
    title: string;
    domain: string;
    baseUrl: string;
};

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE_URL,
        credentials: 'include', // Include cookies for authentication
        prepareHeaders: async (headers, { endpoint }) => {
            if (endpoint === 'logout' || endpoint === 'refreshLogin') {
                const response = await fetch(new URL(`${ENDPOINTS.authenticate()}/session`, new URL(API_BASE_URL, window.location.origin)), { credentials: 'include', cache: 'no-store' });
                if (!response.ok) throw new Error('Could not check sign-in session.');
                const session = await response.json();
                headers.set('X-CSRF-Token', session.csrf);
            }
            headers.set('Accept', '*/*');
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['User', 'AppCards', 'AdminApps', 'Settings'],
    endpoints: (builder) => ({
        // Settings endpoints
        getSettings: builder.query<Settings, void>({
            query: () => ENDPOINTS.settings(),
            providesTags: ['Settings'],
        }),

        // User endpoints
        getProfile: builder.query<User, void>({
            query: () => ENDPOINTS.profile(),
            providesTags: ['User'],
        }),

        // Lightweight session check (does not redirect)
        checkSession: builder.query<{ authenticated: boolean }, void>({
            query: () => `${ENDPOINTS.authenticate()}/session`,
            providesTags: ['User'],
        }),

        // Attempt to silently restore the session server-side (no redirects)
        silentLogin: builder.query<{ authenticated: boolean; restored?: boolean; autoLoginBlocked?: boolean }, void>({
            query: () => `${ENDPOINTS.authenticate()}/silent`,
        }),

        logout: builder.mutation<{ success: string; oidc: boolean; logoutUrl?: string }, void>({
            query: () => ({
                url: `${ENDPOINTS.authenticate()}/logout`,
                method: 'POST',
            }),
            invalidatesTags: ['User', 'AppCards'],
        }),

        refreshLogin: builder.mutation<{ success: string; loginUrl: string }, void>({
            query: () => ({
                url: `${ENDPOINTS.authenticate()}/refresh`,
                method: 'POST',
            }),
        }),

        // App Cards endpoints (for regular users)
        getAppCards: builder.query<{ apps: AppCard[] }, void>({
            query: () => ENDPOINTS.appCards(),
            providesTags: ['AppCards'],
        }),

        // Admin endpoints
        getAdminApps: builder.query<{ apps: AppCardAdmin[] }, void>({
            query: () => ENDPOINTS.admin(),
            providesTags: ['AdminApps'],
        }),

        createApp: builder.mutation<{ id: string; message: string }, Partial<AppCardAdmin>>({
            query: (data) => ({
                url: ENDPOINTS.admin(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        updateApp: builder.mutation<
            { message: string },
            { id: string; data: Partial<AppCardAdmin> }
        >({
            query: ({ id, data }) => ({
                url: ENDPOINTS.adminApp(id),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        deleteApp: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: ENDPOINTS.adminApp(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        reorderApps: builder.mutation<{ message: string }, { id: string; newPosition: number }>({
            query: ({ id, newPosition }) => ({
                url: ENDPOINTS.adminReorder(id, newPosition),
                method: 'PUT',
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),
    }),
});

export const {
    useGetSettingsQuery,
    useGetProfileQuery,
    useCheckSessionQuery,
    useSilentLoginQuery,
    useLazySilentLoginQuery,
    useLogoutMutation,
    useRefreshLoginMutation,
    useGetAppCardsQuery,
    useGetAdminAppsQuery,
    useCreateAppMutation,
    useUpdateAppMutation,
    useDeleteAppMutation,
    useReorderAppsMutation,
} = apiSlice;
